import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, PenLine, TrendingUp, ShieldCheck, Sparkles, Mic, RefreshCw,
  Trophy, LayoutDashboard, Calculator, Eye, Heart, PoundSterling, ShoppingBag,
  FileText, Check, X, Upload, BarChart3, Users,
} from 'lucide-react';

/*
 * HomeToolkitSections — the five new homepage feature sections (static,
 * presentation-only "EXAMPLE DATA" mocks). Self-contained styling using the
 * app's warm theme tokens (Fraunces headings via font-display, Geist body,
 * Geist Mono labels via font-mono, cream backgrounds + magenta accent).
 *
 * Takes `registerHref` so every CTA points at the real free-trial signup.
 */

const CREAM = '#FBF7F0';
const BORDER = '#EAE3D8';
const MAGENTA = '#9D174D';
const TEAL = '#1E7A6B';

const Eyebrow = ({ children }) => (
  <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#9D174D]">{children}</p>
);

const H2 = ({ children, accent }) => (
  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em] text-[#1F1B16] mt-3">
    {children}
    {accent && <span className="italic text-[#1E7A6B]"> {accent}</span>}
  </h2>
);

const ExampleTag = () => (
  <span
    className="absolute top-3 right-3 z-10 inline-flex items-center rounded-md bg-[#1F1B16] text-white text-[9px] font-mono font-bold tracking-[0.14em] uppercase px-2 py-1"
    data-testid="example-data-tag"
  >
    Example data
  </span>
);

const SectionCta = ({ registerHref, label = 'Start free trial', testid, href, note = '3-day free trial · no card required' }) => (
  <div className="mt-7">
    <Link to={href || registerHref}>
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-full bg-[#C0185C] text-white font-semibold px-6 py-3 text-sm shadow-[0_8px_20px_rgba(157,23,77,0.18)] hover:bg-[#a51450] active:translate-y-px transition-colors"
        data-testid={testid}
      >
        {label}
        <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
      </button>
    </Link>
    <p className="font-mono text-[11px] text-[#9D8E7B] mt-3">{note}</p>
  </div>
);

const Section = ({ id, alt, children, testid }) => (
  <section
    id={id}
    className={`border-t border-[${BORDER}] ${alt ? 'bg-[#F6EFE4]' : 'bg-[#FFFDF9]'}`}
    data-testid={testid}
  >
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">{children}</div>
  </section>
);

/* ─────────────────────────── 1. THE WHOLE TOOLKIT ─────────────────────── */

const TOOLKIT = [
  {
    heading: 'Write',
    icon: PenLine,
    tint: 'bg-[#FBE4EE] text-[#9D174D]',
    tools: [
      { icon: Sparkles, name: 'Hooks, angles & scripts', desc: 'Scroll-stopping hooks and a ready-to-film script for any product.' },
      { icon: FileText, name: 'Viral Script Transcriber', desc: 'Paste a winning video and build from its proven structure.' },
      { icon: Mic, name: 'One-click voiceover', desc: 'Turn any script into a natural AI voiceover, ready to drop under footage.', isNew: true, anchor: 'voiceover' },
      { icon: RefreshCw, name: 'Fresh angles on demand', desc: 'Re-spin any hook or angle the moment a video flops.' },
    ],
  },
  {
    heading: 'Grow',
    icon: TrendingUp,
    tint: 'bg-[#DDF0F2] text-[#0B5566]',
    tools: [
      { icon: Trophy, name: 'Top 10 products', desc: 'See the best-selling TikTok Shop products right now, by revenue.', isNew: true },
      { icon: LayoutDashboard, name: 'Creator dashboard', desc: 'Track your views, GMV and commission across your videos in one place.', isNew: true },
    ],
  },
  {
    heading: 'Protect & track',
    icon: ShieldCheck,
    tint: 'bg-[#F6F0E7] text-[#5E574E]',
    tools: [
      { icon: ShieldCheck, name: 'Violation appeal wizard', desc: 'Draft a clear, ready-to-submit appeal when a video gets flagged.', isNew: true },
      { icon: Calculator, name: 'Income calculator', desc: 'Turn your TikTok export into clear monthly earnings at a glance.', isNew: true },
    ],
  },
];

const NewTag = () => (
  <span className="ml-2 inline-flex items-center rounded-full bg-[#9D174D] text-white text-[9px] font-mono font-bold tracking-[0.12em] uppercase px-1.5 py-0.5 align-middle">
    New
  </span>
);

const ToolkitSection = ({ registerHref }) => (
  <Section id="toolkit" testid="home-toolkit">
    <div className="text-center max-w-2xl mx-auto mb-12">
      <Eyebrow>The whole toolkit</Eyebrow>
      <H2 accent="under one roof.">Everything a TikTok Shop creator needs</H2>
      <p className="text-[#6F675D] leading-relaxed mt-4">
        Poettik isn&rsquo;t just a script writer. It&rsquo;s the full workflow: write the content,
        grow with real data, then protect and track what you earn.
      </p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {TOOLKIT.map((col) => (
        <div
          key={col.heading}
          className="rounded-[20px] border border-[#EAE3D8] bg-[#FFFDF9] p-6 shadow-[0_12px_32px_rgba(70,45,20,0.05)]"
          data-testid={`toolkit-col-${col.heading.toLowerCase().replace(/[^a-z]/g, '-')}`}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${col.tint}`}>
              <col.icon className="w-5 h-5" strokeWidth={1.8} />
            </span>
            <h3 className="font-display text-2xl text-[#1F1B16]">{col.heading}</h3>
          </div>
          <ul className="space-y-4">
            {col.tools.map((t) => (
              <li key={t.name} id={t.anchor} className="flex gap-3 scroll-mt-28">
                <span className="mt-0.5 shrink-0 text-[#1E7A6B]">
                  <t.icon className="w-4 h-4" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="text-[15px] font-semibold text-[#1F1B16] leading-tight">
                    {t.name}{t.isNew && <NewTag />}
                  </p>
                  <p className="text-sm text-[#6F675D] leading-relaxed mt-0.5">{t.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="text-center">
      <div className="inline-block"><SectionCta registerHref={registerHref} testid="toolkit-cta" /></div>
    </div>
  </Section>
);

/* ─────────────────────────── 2. TOP 10 PRODUCTS ───────────────────────── */

const TOP_PRODUCTS = [
  { name: 'Nex Playground — Active Play Game Console', sold: '1.5K', commission: '15%', rev: '£310,251' },
  { name: 'Shark TurboBlade Cool + Heat Tower Fan', sold: '1.6K', commission: '5%', rev: '£306,354' },
  { name: 'P.Louise The Bad B*tch Treat Trio', sold: '12.5K', commission: '10%', rev: '£244,251' },
  { name: 'Ninja Thirsti Drinks System', sold: '3.1K', commission: '8%', rev: '£188,900' },
  { name: 'Grande Cosmetics Lash Serum', sold: '9.8K', commission: '20%', rev: '£142,600' },
];
const CATEGORIES = ['All categories', 'Beauty', 'Home', 'Tech', 'Fashion'];

const TopProductsSection = ({ registerHref }) => (
  <Section id="top-products" alt testid="home-top-products">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <Eyebrow>Top 10 products</Eyebrow>
        <H2 accent="before you film.">See what&rsquo;s actually selling</H2>
        <p className="text-[#6F675D] leading-relaxed mt-5">
          The best-selling TikTok Shop products this week, ranked by revenue, with sold counts
          and commission. Pick a proven winner, then write your script around it.
        </p>
        <ul className="space-y-3 mt-6">
          {[
            ['Updated daily', 'Yesterday or the last 7 days, your choice'],
            ['Filter by category', 'Beauty, home, tech, fashion and more'],
            ['Commission at a glance', 'Know the payout before you commit'],
          ].map(([t, d]) => (
            <li key={t} className="flex gap-3 text-sm sm:text-[15px]">
              <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#1E7A6B]" />
              <span><strong className="font-semibold text-[#1F1B16]">{t}</strong><span className="text-[#6F675D]"> — {d}</span></span>
            </li>
          ))}
        </ul>
        <SectionCta registerHref={registerHref} testid="top-products-cta" />
      </div>

      <div className="relative rounded-[22px] border border-[#EAE3D8] bg-[#FFFDF9] p-5 shadow-[0_18px_44px_rgba(70,45,20,0.08)]">
        <ExampleTag />
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag className="w-4 h-4 text-[#1E7A6B]" strokeWidth={1.8} />
          <h3 className="font-display text-lg text-[#1F1B16]">Top 10 Products from this week…</h3>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {CATEGORIES.map((c, i) => (
            <span key={c} className={`rounded-full px-3 py-1 text-xs ${i === 0 ? 'bg-[#1F1B16] text-white' : 'bg-[#F6F0E7] text-[#6F675D] border border-[#EAE3D8]'}`}>{c}</span>
          ))}
        </div>
        <div className="inline-flex rounded-full bg-[#F1E7D7] p-0.5 mb-4">
          <span className="rounded-full px-3 py-1 text-xs text-[#6F675D]">Yesterday</span>
          <span className="rounded-full px-3 py-1 text-xs bg-[#C0185C] text-white font-semibold">Last 7 days</span>
        </div>
        <div className="rounded-[14px] border border-[#EAE3D8] bg-white divide-y divide-[#F0E8DB] overflow-hidden">
          {TOP_PRODUCTS.map((p, i) => (
            <div key={p.name} className="flex items-center gap-3 px-3 py-2.5">
              <span className="w-5 text-center font-display text-base text-[#9D174D] shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-[#1F1B16] truncate">{p.name}</p>
                <p className="text-[11px] text-[#9D8E7B]">{p.sold} sold · {p.commission} commission</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono text-[9px] uppercase tracking-widest text-[#9D8E7B]">Est</p>
                <p className="text-[13px] font-semibold text-[#1F1B16] tabular-nums">{p.rev}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

/* ─────────────────────────── 3. CREATOR DASHBOARD ─────────────────────── */

const STAT_TILES = [
  { icon: Eye, label: 'Views', value: '1.2M', tint: 'text-[#0B5566]' },
  { icon: Heart, label: 'Likes', value: '84.3K', tint: 'text-[#9D174D]' },
  { icon: PoundSterling, label: 'GMV', value: '£18,420', tint: 'text-[#1E7A6B]' },
  { icon: TrendingUp, label: 'Commission', value: '£2,210', tint: 'text-[#5E574E]' },
];
const CHART_PTS = [28, 40, 34, 52, 46, 64, 58, 76, 70, 88];

const DashboardSection = ({ registerHref }) => {
  const w = 300, h = 90;
  const max = Math.max(...CHART_PTS);
  const step = w / (CHART_PTS.length - 1);
  const pts = CHART_PTS.map((v, i) => `${i * step},${h - (v / max) * (h - 10) - 4}`).join(' ');
  return (
    <Section id="creator-dashboard" testid="home-creator-dashboard">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="relative order-2 lg:order-1 rounded-[22px] border border-[#EAE3D8] bg-[#FFFDF9] p-5 shadow-[0_18px_44px_rgba(70,45,20,0.08)]">
          <ExampleTag />
          <div className="flex items-center gap-3 mb-4">
            <span className="w-11 h-11 rounded-full bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center font-display text-lg">S</span>
            <div>
              <p className="font-display text-base text-[#1F1B16] leading-none">@shopwithsana</p>
              <p className="text-[11px] text-[#9D8E7B] mt-1">48.2K followers · UK</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5 mb-4">
            {STAT_TILES.map((s) => (
              <div key={s.label} className="rounded-[12px] border border-[#EAE3D8] bg-white px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <s.icon className={`w-3.5 h-3.5 ${s.tint}`} strokeWidth={1.8} />
                  <span className="font-mono text-[9px] uppercase tracking-widest text-[#9D8E7B]">{s.label}</span>
                </div>
                <p className="font-display text-xl text-[#1F1B16] tabular-nums">{s.value}</p>
              </div>
            ))}
          </div>
          <div className="rounded-[12px] border border-[#EAE3D8] bg-white px-3 py-3 mb-4">
            <p className="font-mono text-[9px] uppercase tracking-widest text-[#9D8E7B] mb-2">Views per day</p>
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[70px]" preserveAspectRatio="none">
              <polyline points={pts} fill="none" stroke="#C0185C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex-1 rounded-[10px] overflow-hidden border border-[#EAE3D8] bg-[#2B2740] aspect-[9/16] relative flex items-end">
                <button type="button" className="w-full bg-[#1F1B16]/70 text-white text-[10px] font-semibold py-1.5">Transcribe</button>
              </div>
            ))}
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <Eyebrow>Creator dashboard</Eyebrow>
          <H2 accent="in one place.">Every video&rsquo;s numbers</H2>
          <p className="text-[#6F675D] leading-relaxed mt-5">
            Paste your TikTok profile and Poettik pulls your recent videos, views, likes, GMV and
            commission into a single dashboard, with a transcribe button on every video.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              ['Your real stats', 'Views, likes, GMV and commission, day by day'],
              ['Recent videos', 'See every post with a one-tap transcribe'],
              ['Spot what works', 'Double down on the videos that actually sell'],
            ].map(([t, d]) => (
              <li key={t} className="flex gap-3 text-sm sm:text-[15px]">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#1E7A6B]" />
                <span><strong className="font-semibold text-[#1F1B16]">{t}</strong><span className="text-[#6F675D]"> — {d}</span></span>
              </li>
            ))}
          </ul>
          <SectionCta registerHref={registerHref} testid="creator-dashboard-cta" />
        </div>
      </div>
    </Section>
  );
};

/* ─────────────────────── 4. VIOLATION APPEAL WIZARD ───────────────────── */

const ViolationSection = ({ registerHref }) => (
  <Section id="violation-wizard" alt testid="home-violation-wizard">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <Eyebrow>Violation appeal wizard</Eyebrow>
        <H2 accent="in minutes.">Got flagged? Fight back</H2>
        <p className="text-[#6F675D] leading-relaxed mt-5">
          When TikTok removes a video or flags your account, Poettik reads the violation, gives you
          an honest verdict on whether it&rsquo;s worth appealing, then writes a clear,
          ready-to-submit appeal for you.
        </p>
        <ul className="space-y-3 mt-6">
          {[
            ['Honest verdict', 'We tell you when an appeal is actually worth it'],
            ['Tailored to real outcomes', 'Results are tuned using creators\u2019 actual appeal results'],
            ['Ready to submit', 'Copy the appeal straight into TikTok'],
            ['Plain English', 'No legal jargon, just what to say'],
          ].map(([t, d]) => (
            <li key={t} className="flex gap-3 text-sm sm:text-[15px]">
              <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#1E7A6B]" />
              <span><strong className="font-semibold text-[#1F1B16]">{t}</strong><span className="text-[#6F675D]"> — {d}</span></span>
            </li>
          ))}
        </ul>
        <SectionCta registerHref={registerHref} testid="violation-wizard-cta" href="/free-violation-appeal" label="Try the violation appeal wizard" note="Your first assessment is free · no account needed" />
      </div>

      <div className="relative rounded-[22px] border border-[#EAE3D8] bg-[#FFFDF9] p-5 shadow-[0_18px_44px_rgba(70,45,20,0.08)]">
        <ExampleTag />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-[14px] border border-[#EAE3D8] bg-white p-4">
            <p className="font-mono text-[9px] uppercase tracking-widest text-[#9D8E7B] mb-3">The violation</p>
            {[
              ['Type', 'Intellectual property'],
              ['Video', 'LED face mask review'],
              ['Reason given', 'Unauthorised use of brand name'],
            ].map(([l, v]) => (
              <div key={l} className="mb-2.5">
                <p className="text-[11px] text-[#9D8E7B]">{l}</p>
                <p className="text-[13px] text-[#1F1B16] font-medium leading-snug">{v}</p>
              </div>
            ))}
          </div>
          <div className="rounded-[14px] border border-[#CDE6DF] bg-[#EEF7F4] p-4">
            <div className="flex items-center justify-between gap-2 mb-2.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-[#1E7A6B] text-white text-[10px] font-semibold px-2 py-0.5">
                <Check className="w-3 h-3" /> Worth appealing
              </span>
              <div className="flex items-center gap-1">
                <span className="text-xl font-display leading-none text-[#15803D]">78%</span>
                <span className="text-[8px] text-[#6F675D] leading-tight">est. chance<br />of success</span>
              </div>
            </div>
            <p className="text-[13px] text-[#1F1B16] leading-relaxed mb-2.5">
              Your review used the product name descriptively, which is generally allowed. A concise
              appeal has a good chance here.
            </p>
            <div className="flex gap-2 rounded-[10px] border border-[#BFE0CB] bg-[#F1F8F3] p-2.5 mb-3">
              <Users className="w-3.5 h-3.5 shrink-0 text-[#1E7A6B] mt-0.5" />
              <p className="text-[11px] text-[#3A463F] leading-snug">
                <span className="font-semibold">Community:</span> 9 of 12 similar appeals from creators were reported successful (75%).
              </p>
            </div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-[#1E7A6B] mb-1.5">Ready-to-submit appeal</p>
            <p className="text-[12px] text-[#3A463F] leading-relaxed italic">
              &ldquo;My video is an honest, independent review that references the product by name only
              to identify it. It does not claim affiliation or misuse any trademark…&rdquo;
            </p>
          </div>
        </div>
        <p className="flex items-center gap-1.5 text-[11px] text-[#9D8E7B] mt-3">
          <ShieldCheck className="w-3 h-3 shrink-0" /> AI-generated guidance — not legal advice. Always review before submitting.
        </p>
      </div>
    </div>
  </Section>
);

/* ─────────────────────────── 5. INCOME CALCULATOR ─────────────────────── */

const EARNINGS_BARS = [
  { m: 'May', v: 42 }, { m: 'Jun', v: 55 }, { m: 'Jul', v: 48 },
  { m: 'Aug', v: 70 }, { m: 'Sep', v: 82 }, { m: 'Oct', v: 95 },
];

const IncomeSection = ({ registerHref }) => (
  <Section id="income-calculator" testid="home-income-calculator">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div className="relative order-2 lg:order-1 rounded-[22px] border border-[#EAE3D8] bg-[#FFFDF9] p-5 shadow-[0_18px_44px_rgba(70,45,20,0.08)]">
        <ExampleTag />
        <div className="flex items-center gap-2 rounded-[12px] border border-dashed border-[#CDB9C6] bg-[#FBF1F5] px-3 py-2.5 mb-4">
          <Upload className="w-4 h-4 text-[#9D174D] shrink-0" strokeWidth={1.8} />
          <span className="text-[13px] text-[#1F1B16] font-medium truncate">tiktok_affiliate_export_oct.csv</span>
          <Check className="w-4 h-4 text-[#1E7A6B] ml-auto shrink-0" />
        </div>
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {[
            ['Total earned', '£6,842'],
            ['This month', '£1,290'],
            ['Avg / video', '£54'],
          ].map(([l, v]) => (
            <div key={l} className="rounded-[12px] border border-[#EAE3D8] bg-white px-3 py-2.5 text-center">
              <p className="font-mono text-[9px] uppercase tracking-widest text-[#9D8E7B]">{l}</p>
              <p className="font-display text-lg text-[#1F1B16] tabular-nums mt-0.5">{v}</p>
            </div>
          ))}
        </div>
        <div className="rounded-[12px] border border-[#EAE3D8] bg-white px-3 py-3 mb-4">
          <p className="font-mono text-[9px] uppercase tracking-widest text-[#9D8E7B] mb-3">Monthly earnings</p>
          <div className="h-24 flex items-end justify-between gap-2">
            {EARNINGS_BARS.map((b) => (
              <div key={b.m} className="flex-1 rounded-t-[4px] bg-[#C0185C]" style={{ height: `${b.v}%` }} />
            ))}
          </div>
          <div className="flex justify-between gap-2 mt-1.5">
            {EARNINGS_BARS.map((b) => (
              <span key={b.m} className="flex-1 text-center font-mono text-[9px] text-[#9D8E7B]">{b.m}</span>
            ))}
          </div>
        </div>
        <div className="rounded-[12px] border border-[#EAE3D8] bg-white px-3 py-3">
          <p className="font-mono text-[9px] uppercase tracking-widest text-[#9D8E7B] mb-2">Affiliate vs ads</p>
          <div className="flex h-3 rounded-full overflow-hidden mb-2">
            <span className="bg-[#1E7A6B]" style={{ width: '72%' }} />
            <span className="bg-[#E3A7C2]" style={{ width: '28%' }} />
          </div>
          <div className="flex justify-between text-[11px] text-[#6F675D]">
            <span><span className="inline-block w-2 h-2 rounded-full bg-[#1E7A6B] mr-1" />Affiliate 72%</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-[#E3A7C2] mr-1" />Ads 28%</span>
          </div>
        </div>
      </div>
      <div className="order-1 lg:order-2">
        <Eyebrow>Income calculator</Eyebrow>
        <H2 accent="not spreadsheets.">See your real earnings</H2>
        <p className="text-[#6F675D] leading-relaxed mt-5">
          Upload your TikTok affiliate export and Poettik turns it into clear running totals, a
          monthly earnings chart and an affiliate-vs-ads split, so you always know what you&rsquo;re making.
        </p>
        <ul className="space-y-3 mt-6">
          {[
            ['Drop in your export', 'Straight from TikTok, no manual typing'],
            ['Running totals', 'Total, this month and average per video'],
            ['Where it comes from', 'Affiliate commission vs ad revenue, split out'],
          ].map(([t, d]) => (
            <li key={t} className="flex gap-3 text-sm sm:text-[15px]">
              <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#1E7A6B]" />
              <span><strong className="font-semibold text-[#1F1B16]">{t}</strong><span className="text-[#6F675D]"> — {d}</span></span>
            </li>
          ))}
        </ul>
        <SectionCta registerHref={registerHref} testid="income-calculator-cta" />
      </div>
    </div>
  </Section>
);

const HomeToolkitSections = ({ registerHref = '/register' }) => (
  <div data-testid="home-toolkit-sections">
    <ToolkitSection registerHref={registerHref} />
    <ViolationSection registerHref={registerHref} />
    <DashboardSection registerHref={registerHref} />
    <TopProductsSection registerHref={registerHref} />
    <IncomeSection registerHref={registerHref} />
  </div>
);

export default HomeToolkitSections;
